/**
 * Pure helpers for the coworker home: merge bookings and guest invitations
 * into one agenda, pick the "Next up" booking, and derive per-guest status.
 *
 * All day/time math happens in the BUILDING timezone:
 * - booking `startAt`/`endAt` are absolute ISO instants (with the building
 *   offset), converted with `Intl` to the building's wall clock;
 * - invitation `visitDate`/`visitTimeBegin`/`visitTimeEnd` are already the
 *   building's wall clock, so they are compared as strings.
 */
import type { GuestInvitationDto, RoomBookingDto } from '@hub-wifi/shared'
import { addDaysInTz, formatHourMinute, nowHmInTz, todayYmdInTz } from '../../lib/calendar-time'

/** Max top-level agenda rows (nested guests don't count). */
export const AGENDA_LIMIT = 6

export type DayKind = 'today' | 'tomorrow' | 'later'

export interface AgendaBookingItem {
  kind: 'booking'
  /** `YYYY-MM-DDTHH:MM` in the building timezone, used for ordering. */
  sortKey: string
  booking: RoomBookingDto
  startHm: string
  endHm: string
  guests: GuestInvitationDto[]
}

export interface AgendaInvitationItem {
  kind: 'invitation'
  sortKey: string
  invitation: GuestInvitationDto
}

export type AgendaItem = AgendaBookingItem | AgendaInvitationItem

export interface AgendaDay {
  ymd: string
  dayKind: DayKind
  items: AgendaItem[]
}

export interface Agenda {
  days: AgendaDay[]
  /** Top-level rows before the cap. */
  total: number
  /** True when rows were dropped by the cap. */
  truncated: boolean
}

export interface NextUp {
  booking: RoomBookingDto
  /** `ongoing`: happening now; otherwise the day the booking starts. */
  state: 'ongoing' | 'today' | 'tomorrow'
  /** Minutes until the booking ends (ongoing) or starts (today/tomorrow). */
  minutes: number
  startHm: string
  endHm: string
  guests: GuestInvitationDto[]
}

function bookingYmd(booking: RoomBookingDto, timeZone: string): string {
  return todayYmdInTz(timeZone, new Date(booking.startAt))
}

function isLive(booking: RoomBookingDto, now: Date): boolean {
  return booking.status === 'confirmed' && new Date(booking.endAt).getTime() > now.getTime()
}

/** Guests linked to `bookingId`, ordered by name. */
export function guestsForBooking(
  invitations: GuestInvitationDto[],
  bookingId: string,
): GuestInvitationDto[] {
  return invitations
    .filter((inv) => inv.roomBookingId === bookingId)
    .sort((a, b) => a.guestName.localeCompare(b.guestName))
}

/** Drops invitations whose visit already ended today (the API returns
 *  everything from today on, by date only). */
export function upcomingInvitations(
  invitations: GuestInvitationDto[],
  timeZone: string,
  now: Date,
): GuestInvitationDto[] {
  const today = todayYmdInTz(timeZone, now)
  const nowHm = nowHmInTz(timeZone, now)
  return invitations.filter(
    (inv) => inv.visitDate > today || (inv.visitDate === today && inv.visitTimeEnd > nowHm),
  )
}

export function dayKindOf(ymd: string, timeZone: string, now: Date): DayKind {
  const today = todayYmdInTz(timeZone, now)
  if (ymd === today) return 'today'
  if (ymd === addDaysInTz(today, 1, timeZone)) return 'tomorrow'
  return 'later'
}

/**
 * The booking for the "Next up" card: one happening now beats any later
 * booking; otherwise the next one starting today or tomorrow. `null` when
 * nothing qualifies (the card is hidden).
 */
export function pickNextUp(
  bookings: RoomBookingDto[],
  invitations: GuestInvitationDto[],
  timeZone: string,
  now: Date,
): NextUp | null {
  const nowMs = now.getTime()
  const live = bookings
    .filter((b) => isLive(b, now))
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())

  const ongoing = live.find((b) => new Date(b.startAt).getTime() <= nowMs)
  const chosen =
    ongoing ?? live.find((b) => dayKindOf(bookingYmd(b, timeZone), timeZone, now) !== 'later')
  if (!chosen) return null

  const startMs = new Date(chosen.startAt).getTime()
  const endMs = new Date(chosen.endAt).getTime()
  const state: NextUp['state'] = ongoing
    ? 'ongoing'
    : dayKindOf(bookingYmd(chosen, timeZone), timeZone, now) === 'today'
      ? 'today'
      : 'tomorrow'
  const target = state === 'ongoing' ? endMs : startMs
  return {
    booking: chosen,
    state,
    minutes: Math.max(0, Math.ceil((target - nowMs) / 60_000)),
    startHm: formatHourMinute(chosen.startAt, timeZone),
    endHm: formatHourMinute(chosen.endAt, timeZone),
    guests: guestsForBooking(invitations, chosen.id),
  }
}

/**
 * Merge bookings and invitations into an agenda grouped by building-local
 * day. Invitations whose `roomBookingId` matches a listed booking nest under
 * it; the rest are standalone rows. `excludeBookingId` (the Next-up booking)
 * is left out together with its guests, which the Next-up card shows.
 */
export function buildAgenda(
  bookings: RoomBookingDto[],
  invitations: GuestInvitationDto[],
  timeZone: string,
  now: Date,
  options: { excludeBookingId?: string; limit?: number } = {},
): Agenda {
  const limit = options.limit ?? AGENDA_LIMIT
  const live = bookings.filter((b) => isLive(b, now))
  const bookingIds = new Set(live.map((b) => b.id))

  const items: AgendaItem[] = []
  for (const booking of live) {
    if (booking.id === options.excludeBookingId) continue
    const startHm = formatHourMinute(booking.startAt, timeZone)
    items.push({
      kind: 'booking',
      sortKey: `${bookingYmd(booking, timeZone)}T${startHm}`,
      booking,
      startHm,
      endHm: formatHourMinute(booking.endAt, timeZone),
      guests: guestsForBooking(invitations, booking.id),
    })
  }
  for (const invitation of upcomingInvitations(invitations, timeZone, now)) {
    if (invitation.roomBookingId && bookingIds.has(invitation.roomBookingId)) continue
    items.push({
      kind: 'invitation',
      sortKey: `${invitation.visitDate}T${invitation.visitTimeBegin}`,
      invitation,
    })
  }

  items.sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0))
  const shown = items.slice(0, limit)

  const days: AgendaDay[] = []
  for (const item of shown) {
    const ymd = item.sortKey.slice(0, 10)
    let day = days[days.length - 1]
    if (!day || day.ymd !== ymd) {
      day = { ymd, dayKind: dayKindOf(ymd, timeZone, now), items: [] }
      days.push(day)
    }
    day.items.push(item)
  }

  return { days, total: items.length, truncated: items.length > shown.length }
}

/** The guest's email or phone (whichever exists), for display under the
 *  name; `null` when the invitation has neither. */
export function guestContact(invitation: GuestInvitationDto): string | null {
  return invitation.guestEmail || invitation.guestPhone || null
}

/** "Ana Souza" -> "AS"; single names give one letter. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}
